import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { activityLog } from "@/features/activityLog";
import { PlanToast } from "@/features/supPlan/ui/SpecialistPlanBar";
import { useRecipesText } from "../../lib/useRecipesText";
import { FOOD_PAID_PARAM } from "../api/foodPack";
import { getQuota } from "../api/recognize";
import { packUntil } from "../lib/quotaText";
import { announceFoodQuota } from "../model/foodAccess";
import { takePackEnd } from "../model/useFoodPackCheckout";

const handled = new Set<string>();

/** Для тестов: забыть обработанные оплаты. */
export const resetFoodPackPurchaseRoot = () => handled.clear();

const SHOW_MS = 5000;

/**
 * Возврат со Stripe после оплаты пакета «+60 распознаваний» — на любой странице (App), как PlanPurchaseRoot у
 * пакетов поиска: сервер выдал пакет до переадресации (возврат, вебхук — один пакет на оплату) и вернул туда, откуда
 * покупали, с ?food_paid=1&session_id=…. Здесь — убрать метки из адреса, спросить счётчик заново, отдать его карточкам
 * «Что в блюде» на странице и сказать «Готово: 3 блюда в день до 3 декабря». Продлевали идущий пакет, а его конец
 * ещё прежний (продление не выдано) — «Распознавания добавятся в течение нескольких минут», а не «Готово» до старой даты.
 */
export const FoodPackPurchaseRoot: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const t = useRecipesText().photo;
    const tRef = useRef(t);
    tRef.current = t;
    const [text, setText] = useState<string | null>(null);
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const paid = params.get(FOOD_PAID_PARAM);
        if (paid === null) return;
        const sid = params.get("session_id") || "";
        params.delete(FOOD_PAID_PARAM);
        params.delete("session_id");
        const rest = params.toString();
        navigate(
            { pathname: location.pathname, search: rest ? `?${rest}` : "", hash: location.hash },
            { replace: true, state: location.state },
        );
        // конец пакета до оплаты — только этой оплате
        const before = takePackEnd();
        // возврат без session_id (старая или набранная руками ссылка) — по записи истории: иначе следующий такой
        // возврат молча пропал бы до перезагрузки; второй прогон эффекта той же записи (StrictMode) — нет
        const id = sid || `nav:${location.key ?? ""}`;
        if (paid !== "1" || handled.has(id)) return;
        handled.add(id);
        const lang = location.pathname.split("/")[1] || "ru";
        const show = (s: string) => {
            setText(s);
            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setText(null), SHOW_MS);
        };
        void getQuota()
            .then((q) => {
                announceFoodQuota(q);
                const end = q.pack?.until ? new Date(q.pack.until).getTime() : NaN;
                // продление: конец пакета не сдвинулся — ещё не выдано
                const until = before != null && !(end > before) ? "" : packUntil(q, lang);
                activityLog.track("payment_result", { kind: "food_pack", status: until ? "paid" : "pending" });
                show(until && q.pack ? tRef.current.packPaid(q.pack.dailyLimit || q.limit, until) : tRef.current.packPaidPending);
            })
            .catch(() => {
                activityLog.track("payment_result", { kind: "food_pack", status: "pending" });
                show(tRef.current.packPaidPending);
            });
        // Только смена адреса: navigate и state — не повод начинать заново.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    return text ? <PlanToast text={text} /> : null;
};
